import { useEffect, useState } from 'react';
import { apiFetch, v1Path } from '../../lib/api';
import { doneToggleStateId } from '../../lib/done-state';
import { countLabel } from '../../lib/progress';
import {
  workItemSubtypeLabel,
  type MemberDto,
  type StateDto,
  type WorkItemFull,
  type WorkItemRef,
  type WorkItemSubtypeDefinition,
} from '../../lib/work-types';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import BlockedToggle from './BlockedToggle';
import { ProgressMeter } from './ProgressParts';
import { LinkedWorkItemCard } from './WorkItemRelations';
import { useOverlays } from './overlay-context';

type Props = {
  item: WorkItemFull;
  projectId: string;
  readOnly?: boolean;
  onUpdated: (item: WorkItemFull) => void;
  subtypes?: WorkItemSubtypeDefinition[];
  members?: MemberDto[];
};

export default function WorkItemFamily({ item, projectId, readOnly, onUpdated, subtypes = [], members }: Props) {
  const parent = item.parent ?? null;
  const children = item.children ?? [];
  const showSubtasks = !readOnly || children.length > 0;
  const { openCreateWorkItem, toast } = useOverlays();
  const [busyId, setBusyId] = useState<string | null>(null);

  // The shared create screen saves the subtask and announces it; pick it up here.
  useEffect(() => {
    const reload = () => {
      void apiFetch<WorkItemFull>(v1Path(`/projects/${projectId}/work-items/${item.id}`)).then(onUpdated).catch(() => undefined);
    };
    window.addEventListener('bb:subtask-created', reload);
    return () => window.removeEventListener('bb:subtask-created', reload);
  }, [projectId, item.id, onUpdated]);

  if (!parent && !showSubtasks) return null;

  const rollup = item.rollup ?? null;
  const openByType = (rollup?.by_subtype ?? []).filter((entry) => entry.open > 0);

  async function refresh() {
    const fresh = await apiFetch<WorkItemFull>(v1Path(`/projects/${projectId}/work-items/${item.id}`));
    onUpdated(fresh);
    window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
  }

  async function toggleDone(child: WorkItemRef) {
    if (busyId) return;
    const markDone = child.state.group !== 'completed';
    setBusyId(child.id);
    try {
      const [states, current] = await Promise.all([
        apiFetch<{ items: StateDto[] }>(v1Path(`/projects/${child.project_id}/states`)),
        apiFetch<WorkItemFull>(v1Path(`/projects/${child.project_id}/work-items/${child.id}`)),
      ]);
      const dest = doneToggleStateId(states.items, markDone);
      if (!dest) {
        toast(markDone ? 'This stream has no Done column.' : 'This stream has no open column.');
        return;
      }
      await apiFetch(v1Path(`/projects/${child.project_id}/work-items/${child.id}/move`), {
        method: 'POST',
        body: JSON.stringify({ version: current.version, state_id: dest }),
      });
      await refresh();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not update the subtask.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="space-y-3">
      {parent ? (
        <div className="space-y-1.5">
          <h2 className="text-sm font-medium">Parent</h2>
          <LinkedWorkItemCard item={parent} />
        </div>
      ) : null}
      {showSubtasks ? (
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-medium">Subtasks</h2>
            {rollup && rollup.total > 0 ? <ProgressMeter counts={rollup} className="max-w-56 flex-1" /> : null}
          </div>
          {openByType.length > 0 ? (
            <p className="text-xs text-muted-foreground">
              Open:{' '}
              {openByType
                .map((entry) => countLabel(entry.open, workItemSubtypeLabel(entry.key, subtypes)))
                .join(' · ')}
            </p>
          ) : null}
          {children.length > 0 ? (
            <ul className="space-y-1.5">
              {children.map((child) => (
                <li key={child.id} className="flex items-center gap-2">
                  <Checkbox
                    checked={child.state.group === 'completed'}
                    disabled={readOnly || busyId === child.id}
                    className="rounded-full"
                    aria-label={child.state.group === 'completed' ? `Mark ${child.title} not done` : `Mark ${child.title} done`}
                    onCheckedChange={() => { void toggleDone(child); }}
                  />
                  <LinkedWorkItemCard item={child} />
                  <BlockedToggle item={child} members={members} readOnly={readOnly} onChanged={refresh} />
                </li>
              ))}
            </ul>
          ) : null}
          {readOnly ? null : (
            <Button type="button" size="sm" variant="outline" onClick={() => openCreateWorkItem({ projectId, parentId: item.id })}>
              Add subtask
            </Button>
          )}
        </div>
      ) : null}
    </section>
  );
}
